import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';
import { UserContextProvider } from '../../../../infrastructure/context/user-context-provider';
import { DetailInfoCardComponent } from '../../../../shared/components/detail-info-card/detail-info-card.component';
import { DetailPair } from '../../../../shared/components/detail-info-card/DetailPair';
import { dateFormat } from '../../../../utilities/DateUtility';
import { AutoHostReviewReasons } from '../../models/AutoHostReviewReasons';
import { ReviewAction } from '../../models/ReviewAction';
import { ScreeningReportQueue } from '../../models/ScreeningReportQueue';
import { ScreeningReportRow } from '../../models/ScreeningReportRow';
import { ScreeningReportService } from '../../services/screening-report.service';

export const CHECK_LABELS: Record<string, string> = {
  identification_document: 'ID Document',
  fraud_detection: 'Fraud Detection',
  background_check: 'Background Check',
  sex_offenders: 'Sex Offender Registry',
  sanctions: 'Sanctions',
  adverse_media: 'Adverse Media',
  blacklist: 'Blacklist',
  payment_verification: 'Payment Verification',
  general_risk: 'General Risk',
  social_verification: 'Social Verification',
  credit_check: 'Credit Check',
  agreement: 'Agreement'
};

export const checkLabel = (name: string): string =>
  CHECK_LABELS[name] ?? name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

type ReasonsError = 'not-found' | 'busy' | 'failed';

@Component({
  selector: 'app-screening-review-panel',
  standalone: true,
  imports: [CommonModule, DetailInfoCardComponent, MatButtonModule, MatCardModule, MatIconModule, MatProgressSpinnerModule, RouterLink],
  templateUrl: './screening-review-panel.component.html',
  styleUrl: './screening-review-panel.component.scss'
})
export class ScreeningReviewPanelComponent implements OnChanges {

  @Input() row?: ScreeningReportRow;
  @Input() queue: ScreeningReportQueue = ScreeningReportQueue.NEEDS_REVIEW;
  @Input() pendingAction: ReviewAction | null = null;

  @Output() keepAsConsider = new EventEmitter<ScreeningReportRow>();
  @Output() markAsPassed = new EventEmitter<ScreeningReportRow>();
  @Output() returnToQueue = new EventEmitter<ScreeningReportRow>();

  readonly queues = ScreeningReportQueue;
  readonly checkLabel = checkLabel;

  guestName = '';
  guestDetails: DetailPair[] = [];
  reasons?: AutoHostReviewReasons;
  loading = false;
  error?: ReasonsError;
  topConcernsExpanded = false;

  private latestRequestId = 0;

  constructor(
    private screeningReportService: ScreeningReportService,
    private userContextProvider: UserContextProvider
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['row']) {
      this.topConcernsExpanded = false;
      this.buildGuestDetails();
      this.loadReasons();
    }
  }

  retry(): void {
    this.loadReasons();
  }

  formatDate(date: string | undefined, includeTime: boolean = false): string {
    return dateFormat(date, this.userContextProvider.region, includeTime);
  }

  private buildGuestDetails(): void {
    const row = this.row;
    this.guestName = row ? `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() : '';
    this.guestDetails = row ? [
      new DetailPair('Organization', row.organizationName),
      new DetailPair('Reservation', row.reservationReferenceId),
      new DetailPair('Arrival', this.formatDate(row.arrivalDate)),
      new DetailPair('Departure', this.formatDate(row.departureDate)),
      new DetailPair('Email', row.email),
      new DetailPair('Phone', row.phone),
      new DetailPair('Screening Completed', this.formatDate(row.dateCompleted, true),
        'When the guest\'s screening finished and the result came back as Consider')
    ] : [];
  }

  private async loadReasons(): Promise<void> {
    const requestId = ++this.latestRequestId;
    this.reasons = undefined;
    this.error = undefined;
    if (!this.row) {
      this.loading = false;
      return;
    }

    this.loading = true;
    try {
      const reasons = await this.screeningReportService.getReviewReasons(this.row.screeningId);
      if (requestId === this.latestRequestId) {
        this.reasons = reasons;
      }
    } catch (e) {
      if (requestId === this.latestRequestId) {
        this.error = this.toError(e);
      }
    } finally {
      if (requestId === this.latestRequestId) {
        this.loading = false;
      }
    }
  }

  private toError(e: unknown): ReasonsError {
    const status = e instanceof HttpErrorResponse ? e.status : undefined;
    if (status === 404) {
      return 'not-found';
    }
    if (status === 429) {
      return 'busy';
    }
    return 'failed';
  }
}
